import { X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button } from './ui';

export type ToastKind = 'success' | 'error' | 'info' | 'warning';
export interface ToastInput {
  kind?: ToastKind;
  title: string;
  message?: string;
  action?: { label: string; onClick: () => void | Promise<unknown> };
  /** Additional actions (e.g. [Add follow-up] [Undo]). */
  actions?: Array<{ label: string; onClick: () => void | Promise<unknown> }>;
  /** ms; errors default to sticky (0) so they don't vanish before being read. */
  duration?: number;
  /** Identical keys replace each other instead of stacking duplicates. */
  key?: string;
}
interface Toast extends ToastInput {
  id: number;
}

const Ctx = createContext<{ push: (t: ToastInput) => void; dismiss: (id: number) => void } | null>(null);
const MAX = 3;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id));
    const tm = timers.current.get(id);
    if (tm) clearTimeout(tm);
    timers.current.delete(id);
  }, []);

  const push = useCallback(
    (input: ToastInput) => {
      const id = ++seq.current;
      const kind = input.kind ?? 'info';
      const key = input.key ?? `${kind}:${input.title}:${input.message ?? ''}`;
      setToasts((list) => {
        const without = list.filter((x) => (x.key ?? `${x.kind}:${x.title}:${x.message ?? ''}`) !== key);
        for (const removed of list.filter((x) => !without.includes(x))) {
          const tm = timers.current.get(removed.id);
          if (tm) clearTimeout(tm);
        }
        return [...without, { ...input, kind, key, id }].slice(-MAX);
      });
      const duration = input.duration ?? (kind === 'error' ? 0 : input.action || input.actions?.length ? 7000 : 4000);
      if (duration > 0) timers.current.set(id, setTimeout(() => dismiss(id), duration));
    },
    [dismiss],
  );

  const value = useMemo(() => ({ push, dismiss }), [push, dismiss]);
  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="toasts" aria-live="polite" aria-relevant="additions">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`} role={t.kind === 'error' ? 'alert' : 'status'}>
            <div className="toast-body">
              <strong>{t.title}</strong>
              {t.message && <span className="muted">{t.message}</span>}
            </div>
            <div className="toast-actions">
              {[...(t.actions ?? []), ...(t.action ? [t.action] : [])].map((a) => (
                <Button
                  key={a.label}
                  size="sm"
                  variant={t.kind === 'error' ? 'primary' : 'default'}
                  onClick={async () => {
                    dismiss(t.id);
                    await a.onClick();
                  }}
                >
                  {a.label}
                </Button>
              ))}
              <Button size="sm" variant="ghost" iconOnly icon={<X size={16} />} aria-label="Dismiss" onClick={() => dismiss(t.id)} />
            </div>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast() {
  const c = useContext(Ctx);
  if (!c) throw new Error('ToastProvider missing');
  return c;
}
